import gsap from "gsap";
import Splide from '@splidejs/splide';
import Loadeer from "loadeer";
import { orderBy } from 'natural-orderby';

import Flip from "gsap/Flip";
import ScrollTrigger from "gsap/ScrollTrigger";
import ScrollToPlugin from "gsap/ScrollToPlugin";

gsap.registerPlugin(Flip, ScrollToPlugin, ScrollTrigger);

// --------------------------------------------------
// 💤 Lazy Loading
// --------------------------------------------------

let lazyLoadedImages = document.querySelectorAll('img.lazy');
const lazy = new Loadeer(lazyLoadedImages, {
  rootMargin: '800px',
  onLoaded: (element) => {
    element.addEventListener('load', () => {
      gsap.to(element, {
        autoAlpha: 1,
        duration: .5,
        ease: 'power1.inOut',
      })
    })
  }
});

if(window.innerWidth < 1000) {
  let images = document.querySelectorAll('.highlight__image');
  images.forEach(image => {
    lazy.triggerLoad(image)
  })
}

// --------------------------------------------------
// 🐭 Cursor
// --------------------------------------------------

const cursor = document.querySelector(".cursor");
const cursorText = document.querySelector(".cursor .text");

// Positioning

const xSet = gsap.quickSetter(cursor, "x", "px");
const ySet = gsap.quickSetter(cursor, "y", "px");
window.addEventListener("pointermove", (e) => {
  if (e.pointerType == "touch") {
    gsap.set(cursor, {
      autoAlpha: 0
    });
  } else {
    gsap.set(cursor, {
      autoAlpha: 1
    });
    xSet(e.x);
    ySet(e.y);
  }
});

// Hide cursor

document.addEventListener("mouseleave", function () {
  gsap.set(cursor, {
    autoAlpha: 0
  });
});

let hoverElements = [];

function handleMouseMove(e) {

  let element = this;
  let text = element.dataset.text;

  if(element.matches('.text-wrap a, .highlight')) {
    if(element.classList.contains('is-prev')) {
      text = 'NEXT'; // RTL ⚠️
    }
    else if(element.classList.contains('is-next')) {
      text = 'PREV'; // RTL ⚠️
    }
    else if(element.classList.contains('is-active')) {
      text = 'OPEN';
    }
    else if(element.classList.contains('highlight')) {
      text = 'PREV';
    }
    else {
      text = 'OPEN';
    }
  }

  gsap.to(cursorText, {
    autoAlpha: 1,
    duration: .25
  });

  cursorText.innerHTML = text;

}

function handleMouseLeave(e) {
  let currentlyHovered = document.querySelector('[data-text]:hover, .text-wrap a:hover, .highlight:hover');
  if(!currentlyHovered) {
    gsap.to(cursorText, {
      autoAlpha: 0,
      duration: .25
    });
  }
}

function cursorHovers() {
  let elements = document.querySelectorAll("[data-text], .text-wrap a, .highlight");

  elements.forEach(element => {

    if(hoverElements.includes(element)) {
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseenter', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseLeave);
    }
    else {
      hoverElements.push(element);
    }

    element.addEventListener("mousemove", handleMouseMove);
    element.removeEventListener('mouseenter', handleMouseMove);
    element.addEventListener("mouseleave", handleMouseLeave);

  });
}
cursorHovers();

// --------------------------------------------------
// ⌨️ Keyboard navigation
// --------------------------------------------------

document.addEventListener('keydown', function(event) {
  let activeSlider = document.querySelector('.highlights');
  if(document.documentElement.dataset.view == 'information' && document.querySelector('.project--open')) {
    activeSlider = document.querySelector('.project--open');
    if(event.key == 'ArrowRight') {
      activeSlider.slider.go('>')
    }
    else if(event.key == 'ArrowLeft') {
      activeSlider.slider.go('<')
    }
  }
  else {
    if(event.key == 'ArrowRight') {
      activeSlider.slider.go('<')
    }
    else if(event.key == 'ArrowLeft') {
      activeSlider.slider.go('>')
    }
  }
});

// --------------------------------------------------
// 🔘 Visual / Information
// --------------------------------------------------

function closeView(view) {
  view = document.querySelector('.view#' + view);
  view.classList.remove('view--open');
}

function openView(view) {
  view = document.querySelector('.view#' + view);
  if(!view.classList.contains('view--open')) {
    let otherview = document.querySelector('.view:not(#' + view.id + ')');
    view.classList.add('view--open');
    closeView(otherview.id);
    unpeekView(otherview.id);
    history.pushState(null, null, '#' + view.id);
    history.pushState(null, null, '#' + view.id);
    document.documentElement.dataset.view = view.id;
  }
  document.documentElement.classList.remove('peeking');
}

function peekView(view) {
  view = document.querySelector('.view#' + view);
  if(!view.classList.contains('view--open')) {
    view.classList.add('view--peek');
    document.documentElement.classList.add('peeking');
  }
}

function unpeekView(view) {
  view = document.querySelector('.view#' + view);
  if(!view.classList.contains('view--open')) {
    view.classList.remove('view--peek');
  }
  document.documentElement.classList.remove('peeking');
}

let viewMenuItems = document.querySelectorAll('a[data-view]');

viewMenuItems.forEach(item => {
  item.addEventListener('click', e => {
    openView(item.dataset.view);
    e.preventDefault();
  })
  item.addEventListener('mouseenter', e => {
    peekView(item.dataset.view);
    e.preventDefault();
  })
  item.addEventListener('mouseleave', e => {
    unpeekView(item.dataset.view);
    e.preventDefault();
  })
})

let aboutMenuItem = document.querySelector('[href="#about"]');
aboutMenuItem.addEventListener('click', e => {
  openView('information');
  e.preventDefault();
})
aboutMenuItem.addEventListener('mouseenter', e => {
  peekView('information');
  e.preventDefault();
})
aboutMenuItem.addEventListener('mouseleave', e => {
  unpeekView('information');
  e.preventDefault();
})

// --------------------------------------------------
// ✨ Highlights slider
// --------------------------------------------------

let slideCount = document.querySelectorAll('.highlights .splide__slide').length

let moved = 0;

let highlights = document.querySelector('.highlights');

highlights.slider = new Splide('.highlights', {
  type: 'loop',
  autoWidth: true,
  focus: 0,
  trimSpace: false,
  updateOnMove: true,
  pagination: false,
  flickMaxPages: 1,
  direction: 'rtl',
  speed: 1000,
  start: slideCount,
  dragMinThreshold: {
    mouse: 0,
    touch: 10,
  },
  arrows: false,
  gap: 0,
  slideFocus: false,
  padding: { left: '5.5rem', right: '5.5rem' },
  breakpoints: {
    1300: {
      padding: { left: '2.5rem', right: '2.5rem' },
    },
    1200: {
      padding: { left: '1.5rem', right: '1.5rem' },
    },
    1000: {
      destroy: true,
    },
  }
});

highlights.slider.on('mounted', function() {
  cursorHovers();
})

let reloadButton = document.querySelector('.header__item--reload');
let reloadButtonA = reloadButton.querySelector('a');

reloadButtonA.addEventListener('click', e => {
  window.location.reload();
  e.preventDefault()
})

gsap.set('.header__item--reload', {
  autoAlpha: 0,
  // yPercent: -300,
})

highlights.slider.on('moved', function() {
  moved++;
  if(moved >= 3) {
    gsap.to('.header__item--reload', {
      autoAlpha: 1,
      duration: 1,
      // yPercent: 0,
      ease: 'power2.inOut'
    })
  }
})

function highlightLazyLoad(activeSlide) {
  if(activeSlide) {
    let prevSlide = activeSlide.previousElementSibling || activeSlide.parentElement.lastElementChild;
    let prevprevSlide = prevSlide.previousElementSibling || prevSlide.parentElement.lastElementChild;
    let nextSlide = activeSlide.nextElementSibling || activeSlide.parentElement.firstElementChild;
    let nextnextSlide = nextSlide.nextElementSibling || nextSlide.parentElement.firstElementChild;
    [activeSlide, prevSlide, nextSlide, prevprevSlide, nextnextSlide].forEach(slide => {
      let image = slide.querySelector('img.lazy');
      let images = document.querySelectorAll('img[data-src="' + image.dataset.src + '"]');
      images.forEach(image => {
        lazy.triggerLoad(image)
      })
    })
  }
}

highlights.slider.on('move moved visible active', function(e) {
  cursorHovers();
})

highlights.slider.on('visible', function(e) {
  highlightLazyLoad(e.slide);
})

highlights.slider.mount();

let highlightsWrap = document.querySelector('.highlights');

highlightsWrap.addEventListener('click', e => {
  
  let target = e.target;

  if(target.matches('.highlight *')) {
    target = target.closest('.highlight');
  }

  let project = document.querySelector(target.dataset.href);

  if(target.classList.contains('is-prev')) {
    highlights.slider.go('<');
    e.preventDefault()
  }
  else if(target.classList.contains('is-next')) {
    highlights.slider.go('>');
    e.preventDefault()
  }
  else if(window.innerWidth < 1000 || (target.classList.contains('is-active') && project)) {

    openProject(project);

    gsap.to(window, {
      scrollTo: {
        y: project,
        offsetY: 60
      },
      ease: "power1.inOut",
      duration: .5,
    });

    e.preventDefault()

  }
  else {
    highlights.slider.go('>');
    e.preventDefault()
  }


})


// --------------------------------------------------
// 🪗 Project accordion
// --------------------------------------------------

let projects = document.querySelectorAll('.project');

if(window.location.hash && window.location.hash != '#!') {
  let project = document.querySelector('.project' + window.location.hash);
  let view = document.querySelector('.view' + window.location.hash);
  if(project) {
    openProject(project);
  }
  else if(view) {
    openView(view.id);
  }
}

function openProject(project) {

  initProjectSlider(project);

  openView('information');

  let projectImages = project.querySelectorAll('.project-slide__image');

  let projectTitle = project.querySelector('.project-title');

  let otherProjects = projects;
  otherProjects.forEach(otherProject => {
    if(otherProject !== project) {
      closeProject(otherProject);
    }
  })

  project.classList.add('project--open');

  setTimeout(() => {
    projectImages.forEach(projectImage => {
      let images = document.querySelectorAll('img[data-src="' + projectImage.dataset.src + '"]');
      images.forEach(image => {
        lazy.triggerLoad(image)
      })
    })
  }, 500)

  history.pushState(null, null, '#' + project.getAttribute('id'));

  projectTitle.dataset.oldText = projectTitle.dataset.text;
  projectTitle.dataset.text = 'CLOSE';
  cursorText.innerHTML = 'CLOSE';

}

function closeProject(project) {

  if(project.classList.contains('project--open')) {
    let projectTitle = project.querySelector('.project-title');
    project.classList.remove('project--open');
    projectTitle.dataset.text = projectTitle.dataset.oldText;
    cursorText.innerHTML = projectTitle.dataset.oldText;
  }

}

projects.forEach(project => {

  let projectTitle = project.querySelector('.project-title');

  projectTitle.addEventListener('click', e => {

    let state = Flip.getState('.project-content', {
      // simple: true
    });
    let startHeight = gsap.getProperty('.work', 'height');

    if(project.classList.contains('project--open')) {
      closeProject(project);
      history.pushState(null, null, '#!');
    }
    else {
      openProject(project);
    }

    let endHeight = gsap.getProperty('.work', 'height');

    let flip = Flip.from(state, {
      duration: .5,
      ease: 'power1.inOut',
    })

    flip.fromTo('.work', {
      height: startHeight,
    }, {
      height: endHeight,
      duration: flip.duration(),
      ease: 'power1.inOut',
      clearProps: 'all',
    }, 0)

    e.preventDefault();
  });

})

// --------------------------------------------------
// ✨ Project sliders
// --------------------------------------------------

function initProjectSlider(project) {
  
  let projectSlider = project.querySelector('.project-slider');
  if(projectSlider) {

    let projectSlides = projectSlider.querySelectorAll('.project-slider__slide');
  
    if(projectSlides.length > 1) {
  
      let index = projectSlider.querySelector('.index');
  
      project.slider = new Splide(projectSlider, {
        type: 'loop',
        pagination: false,
        arrows: false,
        flickMaxPages: 1,
        updateOnMove: true,
        speed: 1000,
        dragMinThreshold: {
          mouse: 0,
          touch: 10,
        },
        gap: 0,
        slideFocus: false,
      });
  
      project.slider.mount();

      cursorHovers();
    
      project.slider.on('active', function() {
        index.innerHTML = project.slider.index + 1;
      })  

      projectSlider.addEventListener('click', e => {
        let target = e.target;
        if(target.matches('.prev, .next')) {
          project.slider.go(target.classList.contains('prev') ? '<' : '>');
          e.preventDefault()
        }
      })
  
    }
  }
  

}  

// --------------------------------------------------
// 🔘 About toggle
// --------------------------------------------------

let name = document.querySelector('.name');
let about = document.querySelector('.about');
let aboutText = document.querySelector('.about__text');
let aboutContact = document.querySelector('.about__contact');
name.addEventListener('click', e => {

  if(about.classList.contains('about--open')) {
    about.classList.remove('about--open');
    name.dataset.text = 'ABOUT';
    cursorText.innerHTML = 'ABOUT';
    gsap.timeline()
    .to(aboutText, {
      autoAlpha: 0,
      duration: .25,
      ease: 'power1.inOut',
    })
    .to(aboutContact, {
      borderTop: '1.5px solid rgba(0,0,0,0)',
      duration: .25,
      ease: 'power1.inOut',
      onComplete: () => {
        gsap.set(aboutText, {
          display: 'none'
        })
      }
    }, 0)
  }
  else {

    about.classList.add('about--open');
    
    gsap.timeline()
    .to(window, {
      scrollTo: {
        y: 0,
      },
      ease: "power1.inOut",
      duration: .25,
    })
    .to(aboutText, {
      autoAlpha: 1,
      duration: .25,
      ease: 'power1.inOut',
      onStart: () => {
        gsap.set(aboutText, {
          display: 'block'
        })
      }
    }, 0)
    .to(aboutContact, {
      borderTop: '1.5px solid rgba(0,0,0,1)',
      duration: .25,
      ease: 'power1.inOut',
    }, 0)

  }

  openView('information');

  e.preventDefault();

});


// --------------------------------------------------
// 🕺 Smooth scroll
// --------------------------------------------------

let anchors = document.querySelectorAll('a[href*="#"]');
anchors.forEach(anchor => {
  anchor.addEventListener('click', function(e) {

    let href = anchor.getAttribute('href');
    let target;
    if(href == '#' || href == '#!') {
      target = document.querySelector('body');
    }
    else {
      target = document.querySelector(href);
    }
    if(target && !target.matches('.project, .open, .close, .view')) {
      gsap.to(window, {
        duration: .5,
        scrollTo: {
          y: target,
        },
        ease: "power1.inOut"
      });
      e.preventDefault();
    }

  });
})


// --------------------------------------------------
// 🃏 Sorting
// --------------------------------------------------

function sortTable(column, direction) {

  let projects = document.querySelectorAll('.project');

  // Save the current state of the projects
  let state = Flip.getState(projects, {
    simple: true
  });

  // Set up array to be sorted
  let projectsSorted = [];

  // Iterate over projects
  projects.forEach(function(project) {

    let sortText = project.querySelector('[data-column="' + column + '"]').innerHTML;

    projectsSorted.push({
      project: project,
      sortText: sortText
    });

  });

  // Sort the array alphabetically
  projectsSorted.sort(function(a, b) {
    if(a.sortText < b.sortText) return -1;
    if(a.sortText > b.sortText) return 1;
    return 0;
  });

  projectsSorted = orderBy(projectsSorted, v => v.sortText, direction);

  // Set order css property
  let c = 0;
  projectsSorted.forEach(function(project) {
    c++;
    project.project.style.order = c;
    project.project.dataset.order = c;
  });


  // Animate the projects from the saved state
  Flip.from(state, {
    ease: "power1.inOut",
    duration: .25,
    // stagger: {
    //   amount: 0.3
    // },
  });

}

let legendColumns = document.querySelectorAll('.legend__column');
legendColumns.forEach(legendColumn => {
  legendColumn.addEventListener('click', e => {

    let columnID = legendColumn.dataset.column;
    let column = document.querySelector('.project-title__column[data-column="' + columnID + '"]');

    if(column) {

      legendColumns.forEach(otherLegendColumn => {
        if(otherLegendColumn !== legendColumn) {
          otherLegendColumn.classList.remove('legend__column--active');
        }
      })

      if(legendColumn.classList.contains('legend__column--active')) {
        if(legendColumn.dataset.direction == 'asc') {
          legendColumn.dataset.direction = 'desc';
        }
        else {
          legendColumn.dataset.direction = 'asc';
        }
      }

      let direction = legendColumn.dataset.direction;

      legendColumn.classList.add('legend__column--active');

      sortTable(columnID, direction);

    }

  });
})

// --------------------------------------------------
// 📱 Mobile overlay
// --------------------------------------------------

let mobileOverlay = document.querySelector('.mobile-overlay');
let mobileToggle = mobileOverlay.querySelector('.mobile-overlay__toggle')
let mobileBlenders = document.querySelectorAll('.mobile-blender')

mobileToggle.addEventListener('click', e => {
  mobileOverlay.classList.toggle('mobile-overlay--open');
  mobileBlenders.forEach(mobileBlender => {
    mobileBlender.style.visibility = mobileOverlay.classList.contains('mobile-overlay--open') ? 'visible' : 'hidden';
  })
  e.preventDefault();
})

// --------------------------------------------------
// 📱 Mobile easter egg
// --------------------------------------------------

let elWebsite = document.querySelector('.mobile-overlay .website')

elWebsite.addEventListener('click', e => {
  
  let screenWidth = window.innerWidth;

  elWebsite.innerHTML = `

    Your ${screenWidth} px screen is not a desktop :’–)

  `
  
})


// --------------------------------------------------
// 🧭 Safari fixes
// --------------------------------------------------

// ScrollTrigger.create({
//   trigger: '.highlights',
//   start: -1000,
//   end: 'bottom top',
//   onToggle: function(self) {
//     if(self.isActive) {
//       console.log('show');
//       gsap.set('.highlights', {
//         autoAlpha: 1
//       });
//     }
//     else {
//       console.log('hide');
//       gsap.set('.highlights', {
//         autoAlpha: 0
//       });
//     }
//   },
// })